export enum PartnershipStatus {
  Pending = "pending",
  PlayingAlone = "playingAlone",
  Active = "active",
  Selected = "selected",
  Character = "character",
  Invalid = "invalid",
}

export class PartnershipStatusHelper {
  static fromFirebaseValue(
    value: string | undefined | null,
  ): PartnershipStatus {
    switch (value) {
      case "pending":
        return PartnershipStatus.Pending;
      case "playingAlone":
        return PartnershipStatus.PlayingAlone;
      case "active":
        return PartnershipStatus.Active;
      case "currentlyPlaying":
      case "selected":
        return PartnershipStatus.Selected;
      case "character":
        return PartnershipStatus.Character;
      case "invalid":
        return PartnershipStatus.Invalid;
      default:
        return PartnershipStatus.Invalid;
    }
  }

  static toFirebaseValue(status: PartnershipStatus): string {
    switch (status) {
      case PartnershipStatus.Pending:
        return "pending";
      case PartnershipStatus.PlayingAlone:
        return "playingAlone";
      case PartnershipStatus.Active:
        return "active";
      case PartnershipStatus.Selected:
        return "currentlyPlaying";
      case PartnershipStatus.Character:
        return "character";
      case PartnershipStatus.Invalid:
        return "invalid";
    }
  }
}

export interface Partnership {
  identifier: string;
  partnerIdentifier?: string;
  status: PartnershipStatus;
  coupledAtTime?: number;
  matchedCardsByIdentifier: Record<string, number>;
  invitationCode: string;
  characterId?: string;
}

export class PartnershipHelper {
  static createEmpty(identifier: string, invitationCode: string): Partnership {
    return {
      identifier,
      status: PartnershipStatus.Invalid,
      matchedCardsByIdentifier: {},
      invitationCode,
    };
  }

  static createPending(
    identifier: string,
    invitationCode: string,
  ): Partnership {
    return {
      identifier,
      status: PartnershipStatus.Pending,
      matchedCardsByIdentifier: {},
      invitationCode,
    };
  }

  static createPlayingAlone(
    identifier: string,
    invitationCode: string,
  ): Partnership {
    return {
      identifier,
      status: PartnershipStatus.PlayingAlone,
      matchedCardsByIdentifier: {},
      invitationCode,
    };
  }

  static isValidForPlay(partnership: Partnership): boolean {
    return (
      partnership.status === PartnershipStatus.Active ||
      partnership.status === PartnershipStatus.Selected ||
      partnership.status === PartnershipStatus.PlayingAlone ||
      partnership.status === PartnershipStatus.Character
    );
  }

  static isCharacter(partnership: Partnership): boolean {
    return partnership.status === PartnershipStatus.Character;
  }

  static isSelected(partnership: Partnership): boolean {
    return partnership.status === PartnershipStatus.Selected;
  }

  static isPending(partnership: Partnership): boolean {
    return partnership.status === PartnershipStatus.Pending;
  }

  static isPlayingAlone(partnership: Partnership): boolean {
    return partnership.status === PartnershipStatus.PlayingAlone;
  }

  static getMatchedCardsIdentifiers(partnership: Partnership): string[] {
    return Object.entries(partnership.matchedCardsByIdentifier)
      .sort((a, b) => b[1] - a[1])
      .map(([cardId]) => cardId);
  }

  static getLatestActivityTimestamp(partnership: Partnership): number {
    const matchTimes = Object.values(partnership.matchedCardsByIdentifier);
    if (matchTimes.length === 0) {
      return partnership.coupledAtTime ?? 0;
    }
    return Math.max(...matchTimes);
  }

  static getNewMatchesCount(
    partnership: Partnership,
    lastVisitTimestamp?: number,
  ): number {
    if (lastVisitTimestamp === undefined || lastVisitTimestamp === null) {
      return 0;
    }

    return Object.values(partnership.matchedCardsByIdentifier).filter(
      (matchTime) => matchTime > lastVisitTimestamp,
    ).length;
  }
}
